import { AfterContentInit, Component, ContentChild, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, from, Observable, switchMap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { FormFieldButtons } from '../form-field-buttons/form-field-buttons.component';
import { FormButton } from '../form-field-button/form-field-button.component';

/**
 * A search fields which allows for mutliple items to be selected.
 */
@Component({
    selector: 'tt-search-multi-select',
    templateUrl: './search-multi-select.component.html',
    styleUrls: ['./search-multi-select.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class SearchMultiSelect<TModelSetting extends 'stringId' | 'id' | 'object', TData = { [key: string]: string | number | boolean }> extends FormFieldBaseComponent implements FormFieldButtons, OnChanges, OnInit, AfterContentInit {
    /**
     * Defines what type should be returned from `ttModel`.
     *
     * * `'stringId'` will provide a string separated by the `ttSeparatorKey`.
     * * `'id'` will provide a list of ids as string.
     * * `'object'` will provide a list of objects as provided to the component.
     *
     * @default 'stringId'
     */
    @Input()
    public ttModelSetting: TModelSetting = 'stringId' as TModelSetting;

    /**
     * A string representing all the selected items in the list. The string is separated by the given separator key in `ttSeparatorKey`.
     */
    @Input()
    public ttModel: TModelSetting extends 'stringId' ? string : TModelSetting extends 'id' ? string[] : TData[] = '' as TModelSetting extends 'stringId' ? string : TModelSetting extends 'id' ? string[] : TData[];

    /**
     * Event emitted when the model has changed.
     */
    @Output()
    public ttModelChange = new EventEmitter<TModelSetting extends 'stringId' ? string : TModelSetting extends 'id' ? string[] : TData[]>();

    /**
     * The key to use to separate between ids selected.
     *
     * @default '; '
     */
    @Input()
    public ttSeparatorKey: string = '; ';

    /**
     * The options to display for the multiselect.
     */
    @Input()
    public ttData: TData[] | (({ value }: { value: string | null }) => Promise<TData[]> | TData[]) = [];

    /**
     * The key of the property to use for the unique id for the item of the items in the list.
     *
     * @default 'item_id'
     */
    @Input()
    public ttDataId: keyof TData = 'item_id' as keyof TData;

    /**
     * The key of the property to use for the display value of the items in the list.
     *
     * @default 'item_name'
     */
    @Input()
    public ttDataName: keyof TData = 'item_name' as keyof TData;

    /**
     * The key of the property to use for the icon of the items in the list.
     *
     * @default 'item_glyphicon'
     */
    @Input()
    public ttDataIcon: keyof TData = 'item_glyphicon' as keyof TData;

    /**
     * Time to wait inbetween each search if `ttData` is of a function type.
     *
     * @default 0
     */
    @Input()
    public set ttSearchDebounce(value: NumberInput) {
        this._searchDebounce = coerceNumberProperty(value);
    }
    public get ttSearchDebounce(): number {
        return this._searchDebounce;
    }
    private _searchDebounce = 0;

    /**
     * Minimum length of searchtext before displaying list of results.
     *
     * @default 0
     */
    @Input()
    public set ttSearchMinLength(value: NumberInput) {
        this._searchMinLength = coerceNumberProperty(value);
    }
    public get ttSearchMinLength(): number {
        return this._searchMinLength;
    }
    private _searchMinLength = 0;

    /**
     * Whether each option can only be selected once.
     *
     * @default true
     */
    @Input()
    public set ttSingleSelection(value: BooleanInput) {
        this._singleSelection = coerceBooleanProperty(value);
    }
    public get ttSingleSelection(): boolean {
        return this._singleSelection;
    }
    private _singleSelection = true;

    @Input()
    public ttButtonParms?: { [key: string]: any };

    @Input()
    public ttButtons?: FormButton[] = [];

    /**
     * Whether to display a generic clear button next to the form field.
     *
     * @emits ttModelChange with an empty string.
     * @default false
     */
    @Input()
    public get ttShowClearButton(): boolean {
        return this._showClearButton;
    }
    public set ttShowClearButton(value: BooleanInput) {
        this._showClearButton = coerceBooleanProperty(value);
    }
    private _showClearButton = false;

    /**
     * Custom item template to use for each option item of the autocomplete.
     */
    @ContentChild('itemTemplate', { static: false })
    public customItemTemplate?: TemplateRef<{ $implicit: TData }>;

    /**
     * The default layout of a item option of the autocomplete, if `itemTemplate` is not provided.
     */
    @ViewChild('defaultItemTemplate', { static: true })
    public defaultItemTemplate?: TemplateRef<{ $implicit: TData }>;

    /**
     * Reference to the inputfield of the search.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef<HTMLInputElement>;

    /**
     * The item option template used in the template html.
     */
    public itemTemplate: TemplateRef<{ $implicit: TData }> | null = null;

    /**
     * The id of the input field.
     */
    public id = crypto.randomUUID();

    /**
     * List of selected ids displayed as chips.
     */
    public selectedIds: string[] = [];

    /**
     * List of objects selected, used if model setting is 'object'.
     */
    public selectedItems: TData[] = [];

    /**
     * The search formcontrol.
     */
    public searchtext = new FormControl('');

    /**
     * The options displayed in the search.
     */
    public data?: Observable<TData[]>;

    public trackItem(_: number, item: TData) {
        return item[this.ttDataId];
    }

    /**
     * Handles when a chip is added from textinput (not autocomplete), when the user presses a separator key (global setup in app.module.ts).
     *
     * @param event the mat chip input event.
     */
    public add(event: MatChipInputEvent): void {
        if (['stringId', 'id'].includes(this.ttModelSetting) || this.ttModelSetting === 'object' /*TODO: predicate for allowing objects not in list.*/) {
            const value = (event.value || '').trim();

            if (value > '' && this.ttModelSetting === 'object') {
                this.selectedItems.push({ [this.ttDataId]: value, [this.ttDataName]: value } as TData);
                this.setSelectedItemsToModel();
            }
            if (this.canAddItem(value)) {
                this.selectedIds.push(value);
                this.setSelectedItemsToModel();
            }

            event.chipInput!.clear();
        }
    }

    /**
     * Handles removal of a chip when button is pressed.
     *
     * @param id the id of the chip to remove.
     */
    public remove(id: string): void {
        const index = this.selectedIds.indexOf(id);
        const itemIndex = this.selectedItems.findIndex((item) => item[this.ttDataId] === id);

        if (index >= 0) {
            this.selectedIds.splice(index, 1);
        }

        if (itemIndex >= 0) {
            this.selectedItems.splice(itemIndex, 1);
        }

        this.setSelectedItemsToModel();
    }

    /**
     * Adds a chip from selection in autocomplete.
     *
     * @param event the autocomplete selection event.
     */
    public selected(event: MatAutocompleteSelectedEvent): void {
        if (this.canAddItem(event.option.value)) {
            this.selectedIds.push(event.option.value[this.ttDataId]);
            this.selectedItems.push(event.option.value);
            this.setSelectedItemsToModel();
        }

        if (!!this.inputRef) this.inputRef.nativeElement.value = '';
        this.searchtext.setValue(null);
    }

    /**
     * Clears all selected ids and reflects the changes.
     */
    public clearInput() {
        this.selectedIds = [];
        this.selectedItems = [];
        this.setSelectedItemsToModel();
    }

    public onBlur(auto: MatAutocomplete) {
        if (!auto?.isOpen) {
            const value = this.searchtext.value;

            if (!!value) {
                if (!!value.trim() && ['stringId', 'id'].includes(this.ttModelSetting)) {
                    this.selectedIds.push(value);
                    this.setSelectedItemsToModel();
                }
            }
        }

        // else {
        //     const activeOption = auto.options.find((option) => option.active === true);

        //     if (!!activeOption) {
        //         this.selectedIds.push(activeOption.value[this.ttDataId]);
        //         this.selectedItems.push(activeOption.value[this.ttDataId]);
        //         this.setSelectedItemsToModel();
        //     }
        // }

        if (!!this.inputRef) this.inputRef.nativeElement.value = '';
        this.searchtext.setValue(null);
    }

    /**
     * Checks whether the given id can be added to selected ids.
     *
     * @param id the id to check if can be added.
     * @returns `true` if the id can be added, `false` if not.
     */
    private canAddItem(id: string) {
        return !!id && ((this.ttSingleSelection && !this.selectedIds.includes(id)) || !this.ttSingleSelection);
    }

    /**
     * Forces change on the search input, causing list to be refreshed.
     */
    private forceSearch() {
        const searchvalue = this.searchtext.value; // store the OG value.

        if (this.inputRef) this.inputRef.nativeElement.style.color = 'transparent !important';

        setTimeout(() => {
            if (!!this.inputRef) this.inputRef.nativeElement.value = '';

            this.searchtext.setValue(crypto.randomUUID(), { emitEvent: false, emitModelToViewChange: false, emitViewToModelChange: false }); // set value to random input, but dont emit event (subscribers wont be notified).

            if (!!this.inputRef) {
                this.inputRef.nativeElement.value = '';
                this.inputRef.nativeElement.style.color = '';
            }

            setTimeout(() => this.searchtext.setValue(searchvalue)); // set the value back after a tick causing event to emitted and subscribes to be notified
        });
    }

    /**
     * Filters the data depending on the how the data is provided to component. \
     * *(Static lists are filtered here, but lists given with functions returning promises assumes the filter happens before resolving)*
     *
     * @param value the search query to filter the list with.
     * @returns list of the filtered data.
     */
    private async filterData(value: string | null): Promise<TData[]> {
        let data: TData[] = [];

        if ((value?.length ?? 0) >= this.ttSearchMinLength) {
            if (this.ttData instanceof Function) {
                const response = await this.ttData({ value: value });

                if (this.validateData(response)) {
                    data = response;
                }
            } else if (this.validateData(this.ttData)) {
                data = !!value ? this.ttData.filter((item) => Object.values(item as any).some((val) => `${val}`.toLowerCase().includes(value.toLowerCase()))) : this.ttData;
            }
        }

        return data.filter((item) => !this.selectedIds.includes(String(item[this.ttDataId])));
    }

    /**
     * Validates that the given data is a list correctly configured according to inputs provided for the `TDate` type.
     *
     * @param data the data to be validated.
     * @returns true of the given data is valid of the given datatype, false if not.
     */
    private validateData(data: unknown): data is TData[] {
        if (Array.isArray(data) && data.every((item) => !!item && typeof item === 'object' && this.ttDataId in item && this.ttDataName in item)) {
            return true;
        } else {
            console.error(`The provided list was not valid according to the configured settings. \n The list provided should be of objects with atleast the following keys \n id: "${String(this.ttDataId)}" \n name: "${String(this.ttDataName)}" \n\n The provided list was: `, data);
            return false;
        }
    }

    /**
     * Combines a string of the selected ids seperated by the separator key and sets it to model.
     */
    private setSelectedItemsToModel() {
        if (['stringId', 'id', 'object'].includes(this.ttModelSetting)) {
            if (this.ttModelSetting === 'stringId') {
                this.ttModel = this.selectedIds.reduce((acc, id) => `${acc}${id}${this.ttSeparatorKey}`, '') as TModelSetting extends 'stringId' ? string : TModelSetting extends 'id' ? string[] : TData[];
            } else if (this.ttModelSetting === 'id') {
                this.ttModel = [...this.selectedIds.map((id) => '' + id)] as TModelSetting extends 'stringId' ? string : TModelSetting extends 'id' ? string[] : TData[];
            } else if (this.ttModelSetting === 'object') {
                this.ttModel = [...this.selectedItems.map((item) => Object.assign({}, item) as TData)] as TModelSetting extends 'stringId' ? string : TModelSetting extends 'id' ? string[] : TData[];
            }
            this.ttModelChange.emit(this.ttModel);
            this.rememberSubject.next(this.ttModel);
        } else {
            console.error('Could nto update selected items to model.');
        }
    }

    /**
     * Splits the ids of the given model using the current set separator key and adds the ids to the selected ids list which will be displayed in chips. Any empty id is ignored.
     *
     * @param model the model to retrieve selected ids from.
     */
    private setSelectedIdsFromModel(model: string) {
        try {
            if (this.ttModelSetting === 'stringId') {
                if (!model || typeof model === 'string') {
                    if (!model || !model.trim()) {
                        this.selectedIds = [];
                    } else if (!!model) {
                        this.selectedIds = model.split(this.ttSeparatorKey).filter((id) => !!id?.trim());
                    }
                } else {
                    throw Error('Type of model does not match type provided in `ttModelSetting`, the default setting is stringId, if another type is attempted to be used the setting must be updated.');
                }
            } else if (this.ttModelSetting === 'id') {
                if (Array.isArray(model) && model.every((item) => typeof item === 'string')) {
                    this.selectedIds = [...model.map((id) => '' + id)];
                } else {
                    throw Error('Type of model does not match type provided in `ttModelSetting`, the default setting is stringId, if another type is attempted to be used the setting must be updated.');
                }
            } else if (this.ttModelSetting === 'object') {
                if (this.validateData(model)) {
                    this.selectedItems = [...model.map((item) => Object.assign({}, item))];
                    this.selectedIds = this.selectedItems.map((item) => '' + item[this.ttDataId]);
                } else {
                    throw Error('Type of model does not match type provided in `ttModelSetting`, the default setting is stringId, if another type is attempted to be used the setting must be updated.');
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async getLastModelState() {
        if (!this.ttOnlyRemember && this.ttRememberId) {
            let laststatus = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;

            if (['id', 'object'].includes(this.ttModelSetting)) {
                try {
                    const json = JSON.parse(laststatus);

                    if (this.ttModelSetting === 'id' && Array.isArray(json) && json.every((id) => typeof id === 'string')) {
                        laststatus = json;
                    } else if (this.validateData(json)) {
                        laststatus = json;
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            this.setSelectedIdsFromModel(laststatus);
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.syncDisabledState();

        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.getLastModelState();
        }
    }

    override async ngOnChanges(changes: SimpleChanges): Promise<void> {
        super.ngOnChanges(changes);

        if (changes['ttSearchDebounce']) {
            this.ttSearchDebounce = changes['ttSearchDebounce'].currentValue;
        }

        if (changes['ttSearchMinLength']) {
            this.ttSearchMinLength = changes['ttSearchMinLength'].currentValue;
        }

        if (changes['ttShowClearButton']) {
            this.ttShowClearButton = changes['ttShowClearButton'].currentValue;
        }

        if (changes['ttDisabled']) {
            this.ttDisabled = changes['ttDisabled'].currentValue;

            this.syncDisabledState();
        }

        if (changes['ttModel']) {
            this.setSelectedIdsFromModel(changes['ttModel'].currentValue);
        }

        if (changes['ttData']) {
            this.data = this.searchtext.valueChanges.pipe(
                debounceTime(this.ttSearchDebounce),
                switchMap((value) => from(this.filterData(value)))
            );

            this.forceSearch();
        }
    }

    override ngAfterContentInit(): void {
        super.ngAfterContentInit();

        this.itemTemplate = this.customItemTemplate || this.defaultItemTemplate || null;
    }

    private syncDisabledState(): void {
        if (this.ttDisabled) {
            this.searchtext.disable({ emitEvent: false });
        } else {
            this.searchtext.enable({ emitEvent: false });
        }
    }
}
